import React from 'react';
import PropTypes from 'prop-types';
import { required } from 'form/validation';
import strings from 'localization/strings';
import lockTypes from 'enums/lockTypes';
import { makeStyles } from 'styles/util';
import { createFormDataForNewLockConfiguration } from 'logic/editStorageSiteLogic';

import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { TextField, showErrorOnBlur } from 'mui-rff';
import MenuItem from '@material-ui/core/MenuItem';
import { FieldArray } from 'react-final-form-arrays';
import AddIcon from '@material-ui/icons/Add';
import IconButton from '@material-ui/core/IconButton';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import ParakeyStorageGroupLockConfigurationDataField from './ParakeyStorageGroupLockConfigurationDataField';
import IloqStorageGroupLockConfigurationDataField from './IloqStorageGroupLockConfigurationDataField';

const useStyles = makeStyles(({ theme }) => ({
    lockConfigurationRow: {
        display: 'flex',
        alignItems: 'center'
    },
    lockType: {
        flex: '1 1 33%',
        marginRight: theme.spacing(1)
    },
    data: {
        flex: '1 1 33%',
        marginRight: theme.spacing(1)
    },
    name: {
        flex: '1 1 33%',
        marginRight: theme.spacing(1)
    },
    removeButton: {
        flex: '0 0 auto'
    }
}));

const StorageGroupLockConfigurationEditor = ({ fieldNamePrefix, storageSiteLockConfiguration }) => {
    const classes = useStyles();

    const getDataLabel = value => lockTypes[value.lockType]?.dataLabel;

    const handleAdd = fields => {
        const newLockConfiguration = createFormDataForNewLockConfiguration();
        fields.push(newLockConfiguration);
    };

    const handleRemove = (fields, index) => {
        fields.remove(index);
    };

    const renderDataField = (fields, name, index) => {
        const parameters = {
            name: `${name}.data`,
            label: getDataLabel(fields.value[index])
        };
        const extendedParameters = {
            ...parameters,
            storageSiteLockConfiguration
        };

        switch(fields.value[index].lockType) {
            case lockTypes.parakey.key:
                return <ParakeyStorageGroupLockConfigurationDataField {...extendedParameters} />;

            case lockTypes.iloq.key:
                return <IloqStorageGroupLockConfigurationDataField {...extendedParameters} />;

            default:
                return (
                    <TextField
                        {...parameters}
                        size="small"
                        variant="outlined"
                        required
                        fieldProps={{ validate: required }}
                        showError={showErrorOnBlur}
                    />
                );
        }
    };

    return (
        <FieldArray name={fieldNamePrefix}>
            {({ fields }) =>
                <>
                    {
                        fields.length === 0 &&
                        (
                            <Typography variant="body1" display="block" gutterBottom>
                                {strings.noLockConfigurations}
                            </Typography>
                        )
                    }
                    {
                        fields.map((name, index) => (
                            <Box key={index} className={classes.lockConfigurationRow}>
                                <Box className={classes.lockType}>
                                    <TextField
                                        select
                                        name={`${name}.lockType`}
                                        label={strings.lockType}
                                        size="small"
                                        variant="outlined"
                                    >
                                        {
                                            Object.values(lockTypes).map(o =>
                                                <MenuItem key={o.key} value={o.key}>{o.title}</MenuItem>
                                            )
                                        }
                                    </TextField>
                                </Box>
                                <Box className={classes.data}>
                                    {renderDataField(fields, name, index)}
                                </Box>
                                {
                                    fields.length > 1 &&
                                    (
                                        <Box className={classes.name}>
                                            <TextField
                                                name={`${name}.name`}
                                                label={strings.lockName}
                                                size="small"
                                                variant="outlined"
                                                required
                                                fieldProps={{ validate: required }}
                                                showError={showErrorOnBlur}
                                            />
                                        </Box>
                                    )
                                }
                                <IconButton
                                    className={classes.removeButton}
                                    onClick={() => handleRemove(fields, index)}
                                >
                                    <DeleteOutlineOutlinedIcon/>
                                </IconButton>
                            </Box>
                        ))
                    }
                    <IconButton
                        color="primary"
                        onClick={() => handleAdd(fields)}
                    >
                        <AddIcon/>
                    </IconButton>
                </>
            }
        </FieldArray>
    );
};

StorageGroupLockConfigurationEditor.propTypes = {
    fieldNamePrefix: PropTypes.string.isRequired,
    storageSiteLockConfiguration: PropTypes.object
};

export default StorageGroupLockConfigurationEditor;
